/**
 * Page scanning utilities for DOM Observer Context
 */

import type { PageAction, PageActionsData, PageHeading, PageMetric, PageStructureData } from './types';

/**
 * Configuration constants for page scanning limits
 */
const MAX_HEADINGS = 10;
const MAX_HEADING_TEXT_LENGTH = 100;
const MAX_METRICS = 15;
const MAX_METRIC_TEXT_LENGTH = 200;
const MAX_METRIC_LABEL_LENGTH = 50;
const MAX_ACTIONS = 20;
const MAX_ACTION_LABEL_LENGTH = 50;

/**
 * Extract page title from heading or document title
 */
export function extractPageTitle(mainContent: Element): string {
  const heading = mainContent.querySelector('h1, h2, [role="heading"]');
  return heading?.textContent?.trim() || document.title;
}

/**
 * Extract all headings from page for structure
 */
export function extractPageHeadings(mainContent: Element): PageHeading[] {
  return Array.from(mainContent.querySelectorAll('h1, h2, h3, h4'))
    .map((h) => ({
      level: h.tagName.toLowerCase(),
      text: h.textContent?.trim(),
    }))
    .filter((h) => h.text && h.text.length > 0 && h.text.length < MAX_HEADING_TEXT_LENGTH);
}

/**
 * Extract visible metrics/stats from page
 */
export function extractPageMetrics(mainContent: Element): PageMetric[] {
  const metrics: PageMetric[] = [];

  // Look for common metric patterns
  mainContent.querySelectorAll('[class*="metric"], [class*="stat"], [class*="kpi"], [class*="card"]').forEach((el) => {
    const text = el.textContent?.trim();
    if (!text || text.length > MAX_METRIC_TEXT_LENGTH) {
      return;
    }

    // Try to extract label and value
    const numbers = text.match(/[\d,.$%]+/g);
    if (numbers && numbers.length > 0) {
      const label = text.replace(/[\d,.$%]+/g, '').trim();
      if (label && label.length > 0 && label.length < MAX_METRIC_LABEL_LENGTH) {
        metrics.push({
          label,
          value: numbers.join(' '),
        });
      }
    }
  });

  return metrics;
}

/**
 * Scan for page headings and structure
 */
export function scanPageStructure(pathname: string): PageStructureData | null {
  // Find main content area
  const mainContent = document.querySelector('main, [role="main"], #app-container');
  if (!mainContent) {
    return null;
  }

  const pageTitle = extractPageTitle(mainContent);
  const headings = extractPageHeadings(mainContent);
  const metrics = extractPageMetrics(mainContent);

  return {
    pageTitle,
    pagePath: pathname,
    headings: headings.slice(0, MAX_HEADINGS),
    metrics: metrics.slice(0, MAX_METRICS),
  };
}

/**
 * Determine action type from button text
 */
export function getActionType(text: string): PageAction['type'] {
  if (/create|add|new/i.test(text)) {
    return 'create';
  }
  if (/edit|update|modify/i.test(text)) {
    return 'edit';
  }
  if (/delete|remove/i.test(text)) {
    return 'delete';
  }
  if (/export|download/i.test(text)) {
    return 'export';
  }
  if (/import|upload/i.test(text)) {
    return 'import';
  }
  if (/search/i.test(text)) {
    return 'search';
  }
  if (/filter/i.test(text)) {
    return 'filter';
  }
  return 'custom';
}

/**
 * Check if a button should be included in actions list
 */
export function shouldIncludeButton(button: HTMLElement): boolean {
  const isVisible = button.offsetParent !== null;

  // Check disabled state - HTMLButtonElement has disabled property, links use aria-disabled
  const isDisabled =
    (button instanceof HTMLButtonElement && button.disabled) || button.getAttribute('aria-disabled') === 'true';

  const isInMainContent = !button.closest('nav, footer, [role="banner"]');

  return isVisible && !isDisabled && isInMainContent;
}

/**
 * Scan for interactive buttons and actions
 */
export function scanPageActions(pathname: string): PageActionsData | null {
  // Look for common action button patterns
  const actionButtons = document.querySelectorAll('button, a[href]');
  const actions: PageAction[] = [];

  actionButtons.forEach((button) => {
    const text = button.textContent?.trim();
    if (!text || text.length > MAX_ACTION_LABEL_LENGTH) {
      return;
    }

    if (shouldIncludeButton(button as HTMLElement)) {
      actions.push({
        label: text,
        type: getActionType(text),
        enabled: true,
      });
    }
  });

  if (actions.length === 0) {
    return null;
  }

  return {
    page: pathname,
    actions: actions.slice(0, MAX_ACTIONS),
    actionCount: actions.length,
  };
}
